/**
 * Site-wide announcement banner. Fetches from `/api/announcements/site`
 * and renders a dismissible strip above the navbar. Dismissed state is
 * stored in sessionStorage so it reappears on a new tab but not on
 * navigation within the same session.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, Info, X, Zap } from 'lucide-react'
import { cn } from '@/shared/lib/cn'
import { api } from '@/shared/lib/api-client'

interface SiteAnnouncement {
  text: string
  type: 'info' | 'success' | 'warning' | 'error'
}

const DISMISS_KEY = 'abdallah-announcement-dismissed'

const TYPE_STYLES = {
  info: 'bg-primary/10 border-primary/30 text-primary',
  success: 'bg-success/10 border-success/30 text-success',
  warning: 'bg-amber-500/10 border-amber-500/30 text-amber-500',
  error: 'bg-destructive/10 border-destructive/30 text-destructive',
}

const TYPE_ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertCircle,
  error: AlertCircle,
}

export function AnnouncementBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })

  const { data } = useQuery({
    queryKey: ['announcements', 'site'],
    queryFn: async () => {
      const res = await api.get<SiteAnnouncement | null>('/announcements/site')
      if (!res.success) return null
      return res.data
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  if (dismissed || !data?.text) return null

  const Icon = TYPE_ICONS[data.type] ?? Zap

  const handleDismiss = () => {
    setDismissed(true)
    try {
      sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      role="banner"
      aria-live="polite"
      className={cn(
        'relative flex items-center justify-center gap-3 border-b px-4 py-2.5 text-sm font-medium',
        TYPE_STYLES[data.type],
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="text-center">{data.text}</span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss announcement"
        className="absolute end-3 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full opacity-70 hover:opacity-100 transition-opacity"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  )
}
